/**
 * The two decisions that need input (f10.4, design D5):
 *
 * * an unmatched account -> Confirm as new client (unit + name), Map to an
 *   existing client, or Exclude;
 * * a new engagement -> Confirm with name, start and end date (Q-f10.4-2),
 *   singly or on a bulk selection (one date pair applied to each).
 *
 * The dialogs validate nothing the server validates: a colliding name, an
 * inactive unit or dates out of order come back as the server's refusal and
 * are shown against the field it named.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import type { UsEngagementsAccount, UsEngagementsEngagement } from '@/api/client';
import { useBusinessUnits, useClients } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { fieldProblem, refusalMessage } from './usEngagementsView';

const ACTIVE = 'ACTIVE';

type AccountChoice = 'CONFIRM' | 'MAP' | 'EXCLUDE';

export interface AccountDecisionInput {
  decision: AccountChoice;
  business_unit_id?: string;
  name?: string;
  code?: string;
  client_id?: string;
}

function initialChoice(account: UsEngagementsAccount): AccountChoice {
  const code = account.decision.code;
  return code === 'MAP' || code === 'EXCLUDE' ? code : 'CONFIRM';
}

export function AccountDecisionDialog({
  account,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  account: UsEngagementsAccount;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (input: AccountDecisionInput) => void;
}) {
  const units = useBusinessUnits();
  const clients = useClients();
  const [choice, setChoice] = useState<AccountChoice>(initialChoice(account));
  const [unitId, setUnitId] = useState(
    account.decided.business_unit?.id ?? account.proposed_business_unit_id ?? '',
  );
  const [name, setName] = useState(account.decided.name ?? account.raw_account_name);
  const [clientId, setClientId] = useState(account.decided.client?.id ?? '');
  const [code, setCode] = useState(account.decided.code ?? '');

  const activeUnits = useMemo(
    () => (units.data?.business_units ?? []).filter((unit) => unit.status.code === ACTIVE),
    [units.data],
  );
  const activeClients = useMemo(
    () =>
      (clients.data?.clients ?? [])
        .filter((client) => client.status.code === ACTIVE)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clients.data],
  );

  const incomplete =
    (choice === 'CONFIRM' && (!unitId || !name.trim())) || (choice === 'MAP' && !clientId);

  const submit = () => {
    if (choice === 'CONFIRM') {
      onSubmit({
        decision: choice,
        business_unit_id: unitId,
        name: name.trim(),
        ...(code.trim() ? { code: code.trim() } : {}),
      });
    } else if (choice === 'MAP') onSubmit({ decision: choice, client_id: clientId });
    else onSubmit({ decision: choice });
  };

  const general = ['name', 'code', 'business_unit_id', 'client_id'].some((key) => fieldProblem(error, key))
    ? null
    : refusalMessage(error);

  return (
    <Modal
      title="Decide this account"
      subtitle={`${account.raw_account_name}${account.sap_id ? ` · SAP ID ${account.sap_id}` : ''}`}
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      submitDisabled={incomplete}
      submitLabel="Save decision"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={general}
    >
      <div className="stack">
        <div className="radio-card-list" role="radiogroup" aria-label="Decision">
          {(
            [
              ['CONFIRM', 'Confirm as new client', 'Created in Administration when you accept the step.'],
              ['MAP', 'Map to existing client', 'The Kimble spelling is added as an alias of that client.'],
              ['EXCLUDE', 'Exclude', 'Its new engagements and elements are excluded with it.'],
            ] as const
          ).map(([value, title, description]) => (
            <label key={value} className={`radio-card${choice === value ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="account-decision"
                value={value}
                checked={choice === value}
                onChange={() => setChoice(value)}
              />
              <span className="rc-text">
                <span className="rc-title">{title}</span>
                <span className="rc-desc">{description}</span>
              </span>
            </label>
          ))}
        </div>

        {choice === 'CONFIRM' && (
          <div className="grid grid-2">
            <DialogField
              label="Client name"
              htmlFor="account-name"
              problem={fieldProblem(error, 'name')}
            >
              <input id="account-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </DialogField>
            <DialogField
              label="Client code"
              htmlFor="account-code"
              problem={fieldProblem(error, 'code')}
              hint="Optional — derived from the name when left blank."
            >
              <input id="account-code" value={code} maxLength={16} onChange={(e) => setCode(e.target.value)} />
            </DialogField>
            <DialogField
              label="Business Unit"
              htmlFor="account-unit"
              problem={fieldProblem(error, 'business_unit_id')}
              hint={
                account.proposed_business_unit_id
                  ? 'Proposed from Proposal: Unit — every element resolves to it.'
                  : 'Pick one: the elements do not resolve to a single unit.'
              }
            >
              <select id="account-unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                <option value="">— choose —</option>
                {activeUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </DialogField>
            {account.unit_candidates.length > 0 && (
              <div className="field span-2">
                <span className="label">Proposal: Unit in the feed</span>
                <ul className="us-eng-candidates">
                  {account.unit_candidates.map((candidate) => (
                    <li key={candidate.raw}>
                      <span className="mono">{candidate.raw}</span>{' '}
                      <span className="hint">
                        {candidate.business_unit ? `→ ${candidate.business_unit.name}` : 'unresolved'} &middot;{' '}
                        {candidate.element_count} elements
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {choice === 'MAP' && (
          <DialogField
            label="Existing client"
            htmlFor="account-client"
            problem={fieldProblem(error, 'client_id')}
          >
            <select id="account-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— choose —</option>
              {activeClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </DialogField>
        )}
      </div>
    </Modal>
  );
}

export interface EngagementConfirmInput {
  name?: string;
  start_date: string;
  end_date: string;
}

/**
 * Confirm one engagement (name + dates) or a bulk selection (dates only; each
 * keeps its default name, the Kimble name minus its `(KEnnnnnn)` suffix).
 */
export function EngagementConfirmDialog({
  engagement,
  count,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  engagement?: UsEngagementsEngagement;
  count?: number;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (input: EngagementConfirmInput) => void;
}) {
  const [name, setName] = useState(engagement?.decided.name ?? engagement?.raw_name ?? '');
  const [start, setStart] = useState(engagement?.decided.start_date ?? '');
  const [end, setEnd] = useState(engagement?.decided.end_date ?? '');

  const incomplete = !start || !end || (engagement !== undefined && !name.trim());
  const named = new Set(['name', 'start_date', 'end_date']);
  const bound = error && [...named].some((key) => fieldProblem(error, key));

  return (
    <Modal
      title={engagement ? `Confirm ${engagement.code}` : `Confirm ${count ?? 0} engagements`}
      subtitle={
        engagement
          ? engagement.raw_name
          : 'One start and end date is applied to each; names keep their Kimble default.'
      }
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          ...(engagement ? { name: name.trim() } : {}),
          start_date: start,
          end_date: end,
        })
      }
      busy={busy}
      submitDisabled={incomplete}
      submitLabel="Confirm"
      submitIcon={<Check aria-hidden="true" />}
      message={bound ? null : refusalMessage(error)}
    >
      {engagement && (
        <DialogField label="Engagement name" htmlFor="engagement-name" problem={fieldProblem(error, 'name')} span>
          <input id="engagement-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </DialogField>
      )}
      <DialogField
        label="Start date"
        htmlFor="engagement-start"
        problem={fieldProblem(error, 'start_date')}
        hint="The feed carries no dates."
      >
        <input id="engagement-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
      </DialogField>
      <DialogField label="End date" htmlFor="engagement-end" problem={fieldProblem(error, 'end_date')}>
        <input id="engagement-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
      </DialogField>
    </Modal>
  );
}
